import Static from './static'
import Integrations from './integration'

import { markRaw } from 'vue'

import { OrderItem, OrderItemType, OrderItemPaymentType } from '@/_api/models/orderItem'

function rawComponent (component) {
  return markRaw(component)
}

export default class PaymentProvider {
  constructor ({ activeIntegrations, customerPaymentMethods, paymentModels }) {
    this.activeIntegrations = activeIntegrations
    this.customerPaymentMethods = customerPaymentMethods
    this.paymentModels = paymentModels

    this.order = ['cash', 'terminal', 'customerPayment', 'giftCard', 'loyaltyPoints', 'customerCredit', 'custom']
  }

  get integrations () {
    const { activeIntegrations } = this

    if (!activeIntegrations) { return [] }

    const integrations = Object.entries(activeIntegrations)

    return integrations
      .map(([guid, data]) => {
        if (Integrations[guid]) {
          const items = []

          for (const component of Integrations[guid].components) {
            items.push(new Integration(component, data))
          }

          return items
        }
      })
      .reduce((acc, items) => {
        if (items) {
          acc.push(...items)
        }

        return acc
      }, [])
  }

  get creditCards () {
    const { customerPaymentMethods } = this

    if (!customerPaymentMethods) { return [] }

    const { creditCards } = customerPaymentMethods

    return creditCards
      .map((data) => {
        return new CreditCard(data)
      })
  }

  get giftCards () {
    const { customerPaymentMethods } = this

    if (!customerPaymentMethods) { return [] }

    const { giftCards } = customerPaymentMethods

    return giftCards
      .map((data) => {
        return new GiftCard(data)
      })
  }

  get customerCredit () {
    const { customerPaymentMethods } = this

    if (!customerPaymentMethods) { return [] }

    const { creditBalance } = customerPaymentMethods

    if (creditBalance > 0) {
      const data = {
        balance: creditBalance,
        balanceLabel: creditBalance,
      }

      return [new CustomerCredit(data)]
    }

    return []
  }

  get loyaltyPoints () {
    const { customerPaymentMethods } = this

    if (!customerPaymentMethods) { return [] }

    const { loyaltyPointBalanceInCurrency } = customerPaymentMethods

    if (loyaltyPointBalanceInCurrency > 0) {
      const data = {
        balance: loyaltyPointBalanceInCurrency,
        balanceLabel: loyaltyPointBalanceInCurrency,
      }

      return [new LoyaltyPoints(data)]
    }

    return []
  }

  get custom () {
    const payments = this.paymentModels

    return payments
      .map(({ title, idPaymentModel }) => {
        const data = {
          title,
          idPaymentModel,
        }

        return new Custom(data)
      })
  }

  get cash () {
    return [new Cash()]
  }

  get giftCardOther () {
    return [new GiftCardOther()]
  }

  get options () {
    const options = [
      ...this.integrations,
      ...this.creditCards,
      ...this.giftCards,
      ...this.customerCredit,
      ...this.loyaltyPoints,
      ...this.custom,
      ...this.cash,
      ...this.giftCardOther,
    ]

    const order = this.order
      .map(type => OrderItemPaymentType[type])

    return options
      .sort((a, b) => {
        return order.indexOf(a.type) - order.indexOf(b.type)
      })
  }
}

class PaymentMethod {
  constructor () {
    this.guid = this.guid()
  }

  guid () {
    let dt = new Date().getTime()
    const guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (dt + Math.random() * 16) % 16 | 0
      dt = Math.floor(dt / 16)
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
    })
    return guid
  }

  get params () {
    return {}
  }

  get label () {
    return 'Payment'
  }

  get isIntegration () {
    return false
  }

  get balance () {
    return this.data.balance
  }

  create (price, tip) {
    const { params, type } = this

    const payment = new OrderItem({
      ...params,
      price,
      tip,
      type: OrderItemType.payment,
      paymentType: type,
    })

    return payment
  }
}

class Integration extends PaymentMethod {
  constructor (component, data) {
    super()

    this.data = data

    this.component = rawComponent(component)
    this.type = OrderItemPaymentType.customerPayment
    this.guid = component.guid
  }

  get isIntegration () {
    return true
  }

  get label () {
    return this.data.name
  }
}

class CreditCard extends PaymentMethod {
  constructor (data) {
    super()

    this.data = data
    this.component = rawComponent(Static.CreditCard)
    this.type = OrderItemPaymentType.customerPayment
  }

  get params () {
    return {
      customerPayment: this.data.idCustomerPayment,
    }
  }

  get label () {
    return `XXXX ${this.data.cardLast4}`
  }
}

class GiftCard extends PaymentMethod {
  constructor (data) {
    super()

    this.data = data
    this.component = rawComponent(Static.GiftCard)
    this.type = OrderItemPaymentType.giftCard
    this.features = ['gift-cards']
  }

  get params () {
    return {
      giftCard: this.data.idGiftCard,
    }
  }

  get label () {
    return `Gift Card (${this.data.number})`
  }
}

class CustomerCredit extends PaymentMethod {
  constructor (data) {
    super()

    this.data = data
    this.component = rawComponent(Static.CustomerCredit)
    this.type = OrderItemPaymentType.customerCredit
  }

  get label () {
    return 'Customer Credit'
  }
}

class LoyaltyPoints extends PaymentMethod {
  constructor (data) {
    super()

    this.data = data
    this.component = rawComponent(Static.LoyaltyPoints)
    this.type = OrderItemPaymentType.loyaltyPoints
    this.features = ['loyalty']
  }

  get label () {
    return 'Loyalty Points'
  }

  get balance () {
    return Math.floor(this.data.balance * 100) / 100
  }
}

class Custom extends PaymentMethod {
  constructor (data) {
    super()

    this.data = data
    this.component = rawComponent(Static.Custom)
    this.type = OrderItemPaymentType.custom
  }

  get params () {
    return {
      title: this.data.title,
      idPaymentModel: this.data.idPaymentModel,
    }
  }

  get label () {
    return this.data.title
  }
}

class Cash extends PaymentMethod {
  constructor () {
    super()

    this.component = rawComponent(Static.Cash)
    this.type = OrderItemPaymentType.cash
    this.change = true
  }

  get label () {
    return 'Cash'
  }
}

class GiftCardOther extends PaymentMethod {
  constructor () {
    super()

    this.component = rawComponent(Static.GiftCardOther)
    this.type = OrderItemPaymentType.giftCard
    this.features = ['gift-cards']
  }

  get params () {
    return {
      giftCard: this.data.idGiftCard,
    }
  }

  get label () {
    return 'Gift card'
  }
}

